import { api } from '../../../servicesGlobais/api'
import type {
  PremiacaoResultado,
  PremiacaoDetalheTime,
  PremiacaoConferenciaTime,
  PremiacaoHistoricoQuinzena,
  PremiacaoRelatorioColaboradorLinha,
} from '../tipos/premiacao'

function normalizeResumoTime(t: any) {
  return {
    timeId: Number(t.timeId ?? t.TimeId ?? 0),
    nomeTime: t.nomeTime ?? t.NomeTime ?? '',
    quantidadePessoas: Number(t.quantidadePessoas ?? t.QuantidadePessoas ?? 0),
    horasTrabalhadasTime: Number(t.horasTrabalhadasTime ?? t.HorasTrabalhadasTime ?? 0),
    totalMinutosFaturados: Number(t.totalMinutosFaturados ?? t.TotalMinutosFaturados ?? 0),
    horasFaturadas: Number(t.horasFaturadas ?? t.HorasFaturadas ?? 0),
    totalValorFaturado: Number(t.totalValorFaturado ?? t.TotalValorFaturado ?? 0),
    percentualAlcancado: Number(t.percentualAlcancado ?? t.PercentualAlcancado ?? 0),
    premiacaoTotal: Number(t.premiacaoTotal ?? t.PremiacaoTotal ?? 0),
    premiacaoPorPessoa: Number(t.premiacaoPorPessoa ?? t.PremiacaoPorPessoa ?? 0),
  }
}

function normalizeResultado(raw: any): PremiacaoResultado {
  return {
    dataInicio: raw.dataInicio ?? raw.DataInicio ?? '',
    dataFim: raw.dataFim ?? raw.DataFim ?? '',
    percentualMaximo: Number(raw.percentualMaximo ?? raw.PercentualMaximo ?? 0),
    times: (raw.times ?? raw.Times ?? []).map(normalizeResumoTime),
    quinzenaId: raw.quinzenaId ?? raw.QuinzenaId ?? undefined,
    dataPagamentoQuinzena: raw.dataPagamentoQuinzena ?? raw.DataPagamentoQuinzena ?? null,
    usuarioPagamentoQuinzenaId: raw.usuarioPagamentoQuinzenaId ?? raw.UsuarioPagamentoQuinzenaId ?? null,
  }
}

function normalizeDetalhe(raw: any): PremiacaoDetalheTime {
  const resumo = raw.resumo ?? raw.Resumo ?? {}
  return {
    resumo: normalizeResumoTime(resumo),
    faturamentos: (raw.faturamentos ?? raw.Faturamentos ?? []).map((f: any) => ({
      idFaturamento: Number(f.idFaturamento ?? f.IdFaturamento ?? 0),
      nf: f.nf ?? f.NF ?? null,
      dataEnvioAnalista: f.dataEnvioAnalista ?? f.DataEnvioAnalista ?? null,
      etapaDescricao: f.etapaDescricao ?? f.EtapaDescricao ?? '',
      projetoNome: f.projetoNome ?? f.ProjetoNome ?? '',
      statusCodigo: f.statusCodigo ?? f.StatusCodigo ?? null,
      statusDescricao: f.statusDescricao ?? f.StatusDescricao ?? null,
      totalMinutos: Number(f.totalMinutos ?? f.TotalMinutos ?? 0),
      valorTotal: Number(f.valorTotal ?? f.ValorTotal ?? 0),
      colaboradoresMinutos: (f.colaboradoresMinutos ?? f.ColaboradoresMinutos ?? []).map((c: any) => ({
        pessoaId: Number(c.pessoaId ?? c.PessoaId ?? 0),
        nome: c.nome ?? c.Nome ?? '',
        totalMinutos: Number(c.totalMinutos ?? c.TotalMinutos ?? 0),
      })),
    })),
    colaboradores: (raw.colaboradores ?? raw.Colaboradores ?? []).map((c: any) => ({
      pessoaId: Number(c.pessoaId ?? c.PessoaId ?? 0),
      nome: c.nome ?? c.Nome ?? '',
      totalMinutos: Number(c.totalMinutos ?? c.TotalMinutos ?? 0),
      horas: Number(c.horas ?? c.Horas ?? 0),
      valorFaturado: Number(c.valorFaturado ?? c.ValorFaturado ?? 0),
    })),
    individual: (raw.individual ?? raw.Individual)
      ? (raw.individual ?? raw.Individual).map((i: any) => ({
          pessoaId: Number(i.pessoaId ?? i.PessoaId ?? 0),
          nome: i.nome ?? i.Nome ?? '',
          tipoContratacao: i.tipoContratacao ?? i.TipoContratacao ?? '',
          horasTrabalhadasPessoa: Number(i.horasTrabalhadasPessoa ?? i.HorasTrabalhadasPessoa ?? 0),
          valorHora: Number(i.valorHora ?? i.ValorHora ?? 0),
          teto: Number(i.teto ?? i.Teto ?? 0),
          premiacaoCalculada: Number(i.premiacaoCalculada ?? i.PremiacaoCalculada ?? 0),
          premiacaoFinal: Number(i.premiacaoFinal ?? i.PremiacaoFinal ?? 0),
          atigiuTeto: Boolean(i.atigiuTeto ?? i.AtigiuTeto ?? false),
          tetoConsecutivoAtual: Number(i.tetoConsecutivoAtual ?? i.TetoConsecutivoAtual ?? 0),
          sinalizado: Boolean(i.sinalizado ?? i.Sinalizado ?? false),
        }))
      : null,
  }
}

function q(ano: number, mes: number, quinzena: number, percentualMaximo?: number) {
  let s = `ano=${ano}&mes=${mes}&quinzena=${quinzena}`
  if (percentualMaximo !== undefined)
    s += `&percentualMaximo=${percentualMaximo}`
  return s
}

export const premiacaoService = {
  async obterPremiacao(ano: number, mes: number, quinzena: number, percentualMaximo = 0.15): Promise<PremiacaoResultado> {
    const raw = await api.get<any>(`/Premiacao?${q(ano, mes, quinzena, percentualMaximo)}`)
    return normalizeResultado(raw)
  },

  async obterDetalhe(ano: number, mes: number, quinzena: number, timeId: number, percentualMaximo = 0.15): Promise<PremiacaoDetalheTime> {
    const raw = await api.get<any>(`/Premiacao/detalhe?${q(ano, mes, quinzena, percentualMaximo)}&timeId=${timeId}`)
    return normalizeDetalhe(raw)
  },

  async listarConferencias(ano: number, mes: number, quinzena: number): Promise<PremiacaoConferenciaTime[]> {
    const raw = await api.get<any[]>(`/Premiacao/conferencias?ano=${ano}&mes=${mes}&quinzena=${quinzena}`)
    return (raw ?? []).map((c: any) => ({
      timeId: Number(c.timeId ?? c.TimeId ?? 0),
      nomeTime: c.nomeTime ?? c.NomeTime ?? '',
      statusCodigo: Number(c.statusCodigo ?? c.StatusCodigo ?? 0),
      statusDescricao: c.statusDescricao ?? c.StatusDescricao ?? '',
      textoDivergencia: c.textoDivergencia ?? c.TextoDivergencia ?? null,
    }))
  },

  async definirConferencia(payload: {
    ano: number
    mes: number
    numeroQuinzena: number
    timeId: number
    status: number
    textoDivergencia?: string | null
  }): Promise<void> {
    await api.put('/Premiacao/conferencias', {
      ano: payload.ano,
      mes: payload.mes,
      numeroQuinzena: payload.numeroQuinzena,
      timeId: payload.timeId,
      status: payload.status,
      textoDivergencia: payload.textoDivergencia ?? null,
    })
  },

  async marcarQuinzenaPaga(ano: number, mes: number, quinzena: number): Promise<void> {
    await api.post('/Premiacao/quinzena/marcar-paga', {
      ano,
      mes,
      numeroQuinzena: quinzena,
    })
  },

  async listarHistorico(take = 24): Promise<PremiacaoHistoricoQuinzena[]> {
    const raw = await api.get<any[]>(`/Premiacao/historico?take=${take}`)
    return (raw ?? []).map((h: any) => ({
      quinzenaId: Number(h.quinzenaId ?? h.QuinzenaId ?? 0),
      ano: Number(h.ano ?? h.Ano ?? 0),
      mes: Number(h.mes ?? h.Mes ?? 0),
      numero: Number(h.numero ?? h.Numero ?? 0),
      descricao: h.descricao ?? h.Descricao ?? '',
      dataInicio: h.dataInicio ?? h.DataInicio ?? '',
      dataFim: h.dataFim ?? h.DataFim ?? '',
      dataPagamento: h.dataPagamento ?? h.DataPagamento ?? null,
      quantidadeConferidos: Number(h.quantidadeConferidos ?? h.QuantidadeConferidos ?? 0),
      quantidadeDivergencias: Number(h.quantidadeDivergencias ?? h.QuantidadeDivergencias ?? 0),
      quantidadeSemRegistroOuPendente: Number(
        h.quantidadeSemRegistroOuPendente ?? h.QuantidadeSemRegistroOuPendente ?? 0,
      ),
    }))
  },

  async baixarRelatorioCsv(
    ano: number,
    mes: number,
    quinzena: number,
    percentualMaximo = 0.15,
  ): Promise<void> {
    const blob = await api.get<Blob>(`/Premiacao/relatorio-colaboradores/export?${q(ano, mes, quinzena, percentualMaximo)}`, {
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `premiacao-colaboradores-${ano}-${String(mes).padStart(2, '0')}-q${quinzena}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  },

  async obterRelatorioColaboradores(
    ano: number,
    mes: number,
    quinzena: number,
    percentualMaximo = 0.15,
  ): Promise<PremiacaoRelatorioColaboradorLinha[]> {
    const raw = await api.get<any[]>(`/Premiacao/relatorio-colaboradores?${q(ano, mes, quinzena, percentualMaximo)}`)
    return (raw ?? []).map((r: any) => ({
      timeId: Number(r.timeId ?? r.TimeId ?? 0),
      nomeTime: r.nomeTime ?? r.NomeTime ?? '',
      pessoaId: Number(r.pessoaId ?? r.PessoaId ?? 0),
      nomeColaborador: r.nomeColaborador ?? r.NomeColaborador ?? '',
      tipoContratacao: r.tipoContratacao ?? r.TipoContratacao ?? '',
      horasTrabalhadasPessoa: Number(r.horasTrabalhadasPessoa ?? r.HorasTrabalhadasPessoa ?? 0),
      valorHora: Number(r.valorHora ?? r.ValorHora ?? 0),
      teto: Number(r.teto ?? r.Teto ?? 0),
      percentualAlcancadoTime: Number(r.percentualAlcancadoTime ?? r.PercentualAlcancadoTime ?? 0),
      premiacaoTotalTime: Number(r.premiacaoTotalTime ?? r.PremiacaoTotalTime ?? 0),
      premiacaoCalculada: Number(r.premiacaoCalculada ?? r.PremiacaoCalculada ?? 0),
      premiacaoFinal: Number(r.premiacaoFinal ?? r.PremiacaoFinal ?? 0),
      atigiuTeto: Boolean(r.atigiuTeto ?? r.AtigiuTeto ?? false),
      tetoConsecutivoAtual: Number(r.tetoConsecutivoAtual ?? r.TetoConsecutivoAtual ?? 0),
      percentualDoTeto:
        r.percentualDoTeto !== undefined && r.percentualDoTeto !== null
          ? Number(r.percentualDoTeto ?? r.PercentualDoTeto)
          : r.PercentualDoTeto !== undefined && r.PercentualDoTeto !== null
            ? Number(r.PercentualDoTeto)
            : null,
    }))
  },
}
